// src/hooks/useAnimatedList.js
import { useEffect, useRef, useState } from 'react';
import { arraysEqual } from '../helpers/objectDiff.js';
import { useSfx } from "../hooks/useSfx.jsx";
import { useRoom } from '../context/RoomContext.jsx';

/**
 * Gestisce una lista animata:
 * - prende "items" come sorgente dati
 * - espone "renderedItems" che sono quelli effettivamente renderizzati
 * - fa animazione OUT -> swap dati -> animazione IN
 *   usando i nodi DOM referenziati in usersRef.current
 */
export const useAnimatedList = (
	items,
	{
		isFinalStep = false,
		getRenderedItems = (it) => it || [],
		animationDurationInMs,
		usersRef,
	}
) => {
	const [renderedItems, setRenderedItems] = useState(() =>
		getRenderedItems(items || [])
	);

	const prevItemsRef = useRef([]);
	const timeoutRef = useRef(null);
	const sfx = useSfx();
	const { roomState } = useRoom();

	// fallback se non passi la durata
	const duration =
		typeof animationDurationInMs === 'number'
			? animationDurationInMs
			: isFinalStep
				? 1200
				: 800;

	useEffect(() => {
		const safeItems = items || [];

		//console.log('[useAnimatedList] items IN:', safeItems);
		//console.log('[useAnimatedList] renderedItems BEFORE effect:', renderedItems);
		//console.log('[useAnimatedList] prevItemsRef BEFORE effect:', prevItemsRef.current);

		// primo giro: sync diretto senza animazioni
		// se non ci sono ancora elementi, non facciamo nulla per evitare loop inutili
		if (!prevItemsRef.current.length && !renderedItems.length) {
			if (!safeItems.length) {
				// niente dati, niente update → nessun loop
				return;
			}

			//console.log('[useAnimatedList] primo giro, FACCIO FILL');
			//const next = getRenderedItems(safeItems);
			//console.log('[useAnimatedList] getRenderedItems result:', next);

			setRenderedItems(getRenderedItems(safeItems));
			prevItemsRef.current = safeItems;
			return;
		}
		const changed = !arraysEqual(prevItemsRef.current, safeItems);
		if (!changed) return;

		if (roomState.isPanelOpen) setTimeout(() => sfx.playTypeEnd(), 1000) 

		// ANIMAZIONE OUT sulla lista ATTUALE (con i vecchi dati)
		if (usersRef?.current) {
			usersRef.current.forEach((node) => {
				if (!node) return;
				if (isFinalStep) {
					node.classList.remove('animate__fadeInLeft');
					node.classList.add('animate__fadeOutLeft');
				} else {
					node.classList.remove('animate__flipInX');
					node.classList.add('animate__flipOutX');
				}
			});
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		// Dopo la durata dell’animazione:
		// 1) aggiorno i dati visibili
		// 2) faccio animazione IN sui nuovi elementi
		timeoutRef.current = setTimeout(() => {
			const nextRendered = getRenderedItems(safeItems);
			setRenderedItems(nextRendered);

			// aspetta il render dei nuovi nodi
			requestAnimationFrame(() => {
				if (usersRef?.current) {
					usersRef.current.forEach((node) => {
						if (!node) return;
						if (isFinalStep) {
							node.classList.remove('animate__fadeOutLeft');
							node.classList.add('animate__fadeInLeft');
						} else {
							node.classList.remove('animate__flipOutX');
							node.classList.add('animate__flipInX');
						}
					});
				}
			});

			prevItemsRef.current = safeItems;
		}, duration);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, isFinalStep, duration]);

	return renderedItems;
};
